import mutationEncrypt from './mutation-encrypt.graphql.js'
import mutationInstall from './mutation-install.graphql.js'
import mutationSave from './mutation-save.graphql.js'
import { useMutation } from 'Data/Api.js'
import { notifyError, useNotifications } from 'Logic/Notifications.js'
import { useSetFlowTo, normalizePath } from 'Simple/Flow.js'
import { useDataSubmit, useDataValue } from 'Simple/Data.js'
import isEqual from 'lodash/isEqual.js'

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props, data) {
  let app = useDataValue({
    context: 'app',
    viewPath: props.viewPath,
  })
  let submit = useDataSubmit({
    context: 'app',
    viewPath: props.viewPath,
  })

  let [, notify] = useNotifications()
  let [, executeMutationInstall] = useMutation(mutationInstall)
  let [, executeMutationEncrypt] = useMutation(mutationEncrypt)
  let [, executeMutationSave] = useMutation(mutationSave)
  let setFlowTo = useSetFlowTo(props.viewPath)

  return async function onSubmit({ value, originalValue, args, change }) {
    switch (args.type) {
      case 'save': {
        let steps = await Promise.all(
          value.steps
            .filter(step => step.value !== null)
            .map(async (item, index) => {
              if (item.type !== 'SECURE_STRING') return item

              let value
              if (item.value.startsWith('ENCRYPTED:')) {
                value = originalValue.steps[index].value.replace(
                  'ENCRYPTED:',
                  ''
                )
              } else {
                let mutationEncryptResponse = await executeMutationEncrypt({
                  value: item.value,
                })
                value = mutationEncryptResponse.data.encrypt.value
              }
              return { ...item, value }
            })
        )
        let properties = Object.fromEntries(
          steps
            .filter(step => step.value !== null)
            .map(step => [step.key, step.value])
        )

        let originalProperties = Object.fromEntries(
          originalValue.steps
            .filter(step => step.value !== null)
            .map(step => [step.key, step.value])
        )

        if (
          !app.company.properties ||
          !isEqual(properties, originalProperties)
        ) {
          let mutationResponse = await executeMutationSave({
            id: app.company.id,
            properties,
          })

          if (mutationResponse.error) {
            return notify(
              notifyError(`There was a problem saving the properties`)
            )
          }
        }

        if (value.current_index === value.steps.length - 1) {
          let mutationResponse = await executeMutationInstall({
            app_id: app.id,
          })
          if (mutationResponse.error) {
            return notify(
              notifyError(`There was a problem with the installation`)
            )
          }

          if (app.connection) {
            if (app.connections.length === 0) {
              switch (app.connection.type) {
                case 'API_TOKEN': {
                  setFlowTo([
                    normalizePath(props.viewPath, '../No'),
                    normalizePath(
                      props.viewPath,
                      '../../../Connections/Content/AddAction/ApiToken'
                    ),
                  ])
                  return
                }

                case 'HTTP_JWT': {
                  submit({ type: 'connect-http-jwt' })
                  return
                }

                case 'OAUTH2':
                case 'OAUTH2_AUTHORIZATION_CODE': {
                  setFlowTo([
                    normalizePath(props.viewPath, '../No'),
                    normalizePath(
                      props.viewPath,
                      '../../../Connections/Content/AddAction/OAuth2'
                    ),
                  ])
                  return
                }

                case 'OAUTH2_CLIENT_CREDENTIALS': {
                  submit({ type: 'connect-oauth2-client-credentials' })
                  return
                }

                default: {
                  return
                }
              }
            }
          }

          setFlowTo(normalizePath(props.viewPath, '../No'))
        } else {
          change(next => {
            next.current_index = value.current_index + 1
          })
        }

        return
      }

      default: {
        return
      }
    }
  }
}
